import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import ReactSelect, { StylesConfig } from 'react-select';

import ClickableIcon from '~components/ClickableIcon';
import { faRefresh } from '~components/icon';

import { InputProps } from '../props';

export interface Option {
  value: string | number;
  label: string;
  [key: string]: unknown;
}

interface GateOptions {
  getValidOptions: (value: any, allOptions: Option[]) => Option[];
}

interface ComponentProps extends InputProps {
  ignorePlaceholder?: boolean;
  styles?: StylesConfig<Option>;
  getOptionStyle?: (option: Option) => React.CSSProperties;
  clearable?: boolean;
  gateOptions?: GateOptions;
}

const Select: React.FC<ComponentProps> = ({
  id,
  value,
  placeholder,
  noOptionsMessage,
  ignorePlaceholder = false,
  className,
  disabled = false,
  options,
  selectorOptions,
  selectorActions,
  selectorParams,
  onChange,
  styles,
  getOptionStyle,
  clearable = true,
  manualReload = false,
  dataCy,
  gateOptions,
}) => {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const selectorData = selectorOptions ? useAppSelector(selectorOptions) : null;

  const selectOptions = useMemo(() => {
    const sourceData = options || selectorData || [];
    return sourceData.map((item: any) => ({
      value: item.id,
      label: item.name,
      ...item,
    }));
  }, [options, selectorData]);

  const filteredOptions = useMemo(() => {
    if (!gateOptions) return selectOptions;
    const rest = gateOptions.getValidOptions(
      selectOptions.find((option) => option.id === value),
      selectOptions,
    );
    return rest;
  }, [gateOptions, selectOptions, value]);

  const customStyles: StylesConfig<Option> = useMemo(
    () => ({
      option: (provided, state) => ({
        ...provided,
        ...(getOptionStyle ? getOptionStyle(state.data as Option) : {}),
      }),
      ...styles,
    }),
    [getOptionStyle, styles],
  );

  const refreshOptions = useCallback(async () => {
    if (isRefreshing || !selectorActions) return;

    setIsRefreshing(true);
    try {
      await dispatch(selectorActions(selectorParams));
    } catch (error) {
      console.error(`Error loading options for ${id}:`, error);
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, id, isRefreshing, selectorActions, selectorParams]);

  return (
    <div className="align-items-center row" data-cy={`${id}-select`}>
      <div className="col">
        <ReactSelect
          id={id}
          name={id}
          value={filteredOptions.find((option) => option.value === value) || null}
          isDisabled={disabled || filteredOptions.length === 0}
          isClearable={clearable}
          options={filteredOptions}
          className={className}
          classNamePrefix="form-input-select"
          data-cy={dataCy}
          onChange={(option: Option) => {
            onChange?.(id, option?.value ?? null);
          }}
          placeholder={
            !ignorePlaceholder ? (placeholder ?? (filteredOptions.length === 0 ? noOptionsMessage : '-')) : undefined
          }
          styles={customStyles}
        />
      </div>
      {manualReload && (
        <div className="col-sm-1 ms-2">
          <ClickableIcon
            id={id}
            icon={faRefresh}
            size="small"
            color="success"
            onClick={refreshOptions}
            dataCy={`${id}-reload`}
            disabled={isRefreshing}
            toolTip="Reload"
          />
        </div>
      )}
    </div>
  );
};

export default Select;
